/**
 * When the call pane has to keep asking, and when it may stop.
 *
 * Hanging up is not the end of the work on a call. Twilio finishes the
 * recording, the API downloads it and deletes Twilio's copy, and only then does
 * the transcription queue see it - three steps, none of which the browser is
 * told about, because this repository has no socket and no SSE anywhere. So the
 * pane asks again. Nothing here polls a call the server has finished with, which
 * is the whole reason the decision is a function rather than a status check
 * spread through an effect.
 *
 * The awkward case is the one the two status columns cannot express: for the
 * first seconds after a hangup the row still reads "none" / "none", because
 * Twilio's recording callback has not landed and there is therefore nothing to
 * queue yet. Read literally that says "this call has no recording and never
 * will", which is exactly the wrong answer to stop asking on - it is what left
 * the pane sitting on "the transcript is being prepared" until somebody reloaded
 * the page by hand. Hence the grace window, and hence its ceiling: a call really
 * placed without recording must not be polled for ten minutes to find that out.
 */

/** How often the pane asks again while a call is still settling. */
export const WATCH_INTERVAL_MS = 5_000;
/**
 * The ceiling on one watch. Everything downstream of a hangup normally lands
 * inside a minute or two; past this the answer is that something is broken, and
 * a browser tab left open overnight must not keep asking about it.
 */
export const WATCH_MAX_MS = 10 * 60 * 1000;
/**
 * How long a just-ended call is given to grow a `recordingStatus` before its
 * silence is believed. Twilio's callback normally arrives within seconds; this
 * is generous enough to cover a slow one and short enough that a call placed
 * with recording off costs a handful of requests, not two hundred.
 */
export const SETTLE_GRACE_MS = 3 * 60 * 1000;

/** The fields the decision reads. A subset of `CallDetail`, so it can be tested
 *  without inventing a whole call. */
export interface WatchableCall {
  recordingStatus: string;
  transcriptStatus: string;
  /** What THIS call was placed with. Null on rows older than the dial pad's
   *  per-call toggles, which followed the account setting of the day. */
  recordEnabled: boolean | null;
  startedAt: string;
  endedAt: string | null;
}

/**
 * True while something outside the browser is still expected to change this row.
 *
 * The order of the tests is the meaning: an explicit "in progress" status is
 * believed first, a terminal one ends it, and only a row that says neither falls
 * through to the clock.
 */
export function isSettling(call: WatchableCall | null, now: number): boolean {
  if (!call) return false;

  // Somebody is demonstrably working on it.
  if (call.transcriptStatus === "pending" || call.transcriptStatus === "running") return true;
  if (call.recordingStatus === "pending") return true;

  // Terminal: whatever was going to happen has happened. "failed" counts - the
  // retry button re-arms the row and flips the status back to "pending", which
  // starts a fresh watch on its own.
  if (call.transcriptStatus === "done" || call.transcriptStatus === "failed") return false;
  // A stored recording with no transcript queued behind it is a call whose
  // transcript was switched off. The audio is here; nothing else is coming.
  if (call.recordingStatus === "stored" || call.recordingStatus === "failed") return false;

  // A call placed with recording off has answered the question already, and no
  // callback is on its way to contradict it.
  if (call.recordEnabled === false) return false;

  // Silent row: believe the silence only after the grace window. `endedAt` is
  // written by the status webhook and can lag the hangup by a second or two, so
  // a row that has not got one yet is measured from when it started - which for
  // a call still in progress is the correct reading anyway.
  const since = Date.parse(call.endedAt ?? call.startedAt);
  return Number.isFinite(since) && now - since < SETTLE_GRACE_MS;
}
